import {
	ArrowRight,
	Brain,
	CheckCircle2,
	Loader2,
	RefreshCw,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/primitives";

interface ConversationEndSummaryProps {
	/**
	 * Summary description to display
	 */
	summary: {
		title?: string;
		description: string;
	};
	/**
	 * Callback when user chooses to continue (Yes)
	 */
	onContinue: () => void;
	/**
	 * Callback when user chooses to dive deeper
	 */
	onDiveDeeper: () => void;
	/**
	 * Callback when user chooses to go down a different path
	 */
	onDifferentPath: () => void;
	/**
	 * Whether any action is in progress
	 */
	isLoading?: boolean;
	/**
	 * Whether the summary is being processed (accept action)
	 */
	isProcessingSummary?: boolean;
	/**
	 * Whether we're already diving deeper (only show continue option)
	 */
	isDivingDeeper?: boolean;
}

const markdownPlugins = [remarkGfm];

export function ConversationEndSummary({
	summary,
	onContinue,
	onDiveDeeper,
	onDifferentPath,
	isLoading = false,
	isProcessingSummary = false,
	isDivingDeeper = false,
}: ConversationEndSummaryProps) {
	return (
		<Card className="w-full max-w-4xl mx-auto border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
						<Brain className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold">
							Conversation Summary
						</CardTitle>
					</div>
				</div>
			</CardHeader>

			<CardContent className="pt-6">
				<div className="space-y-6">
					{/* Summary Display */}
					<div className="bg-muted/30 dark:bg-muted/20 border rounded-lg p-6">
						{summary.title && (
							<h3 className="text-lg font-semibold mb-3 text-foreground">
								{summary.title}
							</h3>
						)}
						<div
							className="prose prose-sm dark:prose-invert max-w-none"
							data-testid="reflection-summary"
						>
							<ReactMarkdown remarkPlugins={markdownPlugins}>
								{summary.description}
							</ReactMarkdown>
						</div>
					</div>

					{/* Options */}
					<div className="space-y-4">
						{isDivingDeeper ? (
							<>
								<p className="text-sm font-medium text-foreground mb-4">
									Ready to continue?
								</p>
								<div className="space-y-3">
									{/* Only Continue option when diving deeper */}
									<Button
										onClick={onContinue}
										disabled={isLoading || isProcessingSummary}
										className="w-full justify-start h-auto py-4 px-4 text-left"
										variant="default"
									>
										<div className="flex items-center gap-3 w-full">
											{isProcessingSummary ? (
												<Loader2 className="h-5 w-5 flex-shrink-0 animate-spin" />
											) : (
												<CheckCircle2 className="h-5 w-5 flex-shrink-0" />
											)}
											<div className="flex-1">
												<div className="font-medium">
													{isProcessingSummary
														? "Processing..."
														: "Continue to the next step"}
												</div>
												<div className="text-sm opacity-80 font-normal">
													{isProcessingSummary
														? "Generating your reflection summary"
														: "Proceed with your reflection"}
												</div>
											</div>
											{!isProcessingSummary && (
												<ArrowRight className="h-4 w-4 flex-shrink-0" />
											)}
										</div>
									</Button>
								</div>
							</>
						) : (
							<>
								<p className="text-sm font-medium text-foreground mb-4">
									Are you happy to continue with this topic?
								</p>
								<div className="space-y-3">
									{/* Option 1: Yes, Continue */}
									<Button
										onClick={onContinue}
										disabled={isLoading || isProcessingSummary}
										className="w-full justify-start h-auto py-4 px-4 text-left"
										variant="default"
									>
										<div className="flex items-center gap-3 w-full">
											{isProcessingSummary ? (
												<Loader2 className="h-5 w-5 flex-shrink-0 animate-spin" />
											) : (
												<CheckCircle2 className="h-5 w-5 flex-shrink-0" />
											)}
											<div className="flex-1">
												<div className="font-medium">
													{isProcessingSummary ? "Processing..." : "Yes"}
												</div>
												<div className="text-sm opacity-80 font-normal">
													{isProcessingSummary
														? "Generating your reflection summary"
														: "Continue to the next step"}
												</div>
											</div>
											{!isProcessingSummary && (
												<ArrowRight className="h-4 w-4 flex-shrink-0" />
											)}
										</div>
									</Button>

									{/* Option 2: Dive Deeper */}
									<Button
										onClick={onDiveDeeper}
										disabled={isLoading || isProcessingSummary}
										className="w-full justify-start h-auto py-4 px-4 text-left"
										variant="outline"
									>
										<div className="flex items-center gap-3 w-full">
											<Brain className="h-5 w-5 flex-shrink-0" />
											<div className="flex-1">
												<div className="font-medium">
													I'd like to dive a little deeper first
												</div>
												<div className="text-sm opacity-80 font-normal">
													Ask 4-5 more questions on this topic
												</div>
											</div>
										</div>
									</Button>

									{/* Option 3: Different Path */}
									<Button
										onClick={onDifferentPath}
										disabled={isLoading || isProcessingSummary}
										className="w-full justify-start h-auto py-4 px-4 text-left"
										variant="outline"
									>
										<div className="flex items-center gap-3 w-full">
											<RefreshCw className="h-5 w-5 flex-shrink-0" />
											<div className="flex-1">
												<div className="font-medium">
													No, I'd like to go down a different path
												</div>
												<div className="text-sm opacity-80 font-normal">
													Start a new conversation on a different topic
												</div>
											</div>
										</div>
									</Button>
								</div>
							</>
						)}
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
